import { useCallback, useMemo, useState, type ReactNode } from "react";
import AddIcon from "@mui/icons-material/Add";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ExportMenu from "@/components/appointmentRequest/exportMenu";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import {
  formatDateValue,
  getLmsCourseOverviewRoute,
  getLmsDisplayText,
  getLmsManagerRequestParameters,
  LmsMode,
} from "@/components/lms/lms.utils";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import {
  requiresLmsCompanySelection,
  resolveOverviewMetricsScope,
} from "@/components/lms/lmsOverviewMetricsHelpers";
import LmsEnrolmentIntervalEditModal from "@/components/lms/lmsEnrolmentIntervalEditModal";
import LmsIntervalValue, {
  getEnrolmentTableIntervalInheritedFrom,
} from "@/components/lms/lmsIntervalValue";
import type { QuickSearchSelectionValidationState } from "@/types/QuickSearch";
import {
  getLmsSelectionBlockedMessage,
  LMS_START_VERTRAG_SELECTION_ENRICHMENT,
} from "@/components/lms/lmsQuickSearchEligibility";

interface Props {
  courseId?: string;
  mode?: LmsMode;
  includeCourseColumn?: boolean;
  participantId?: string;
  subjectOverviewMode?: boolean;
  onCreate?: () => void;
  customTopToolBarAction?: () => ReactNode;
  refreshKey?: number;
  onRefreshed?: () => void;
  onError?: (error: any) => void;
  showViewCourseButton?: boolean;
  canCreateEnrolments?: boolean;
  createEnrolmentsDisabledMessage?: string;
}

export default function LmsEnrolmentsTable({
  courseId,
  mode = "administrator",
  includeCourseColumn = false,
  participantId,
  subjectOverviewMode = false,
  onCreate,
  customTopToolBarAction,
  refreshKey = 0,
  onRefreshed,
  onError,
  showViewCourseButton = true,
  canCreateEnrolments = true,
  createEnrolmentsDisabledMessage,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const subjectSearch = useSelector((state: RootState) => state.subjectSearch);
  const loginState = useSelector((state: RootState) => state.login);
  const { handleError: handleLocalError, errorModal } = useErrorModal();
  const [selectedEnrolmentForEdit, setSelectedEnrolmentForEdit] =
    useState<any>(null);
  const [exportAllCompanies, setExportAllCompanies] = useState(false);
  const [companySelectionValidationState, setCompanySelectionValidationState] =
    useState<QuickSearchSelectionValidationState>("idle");
  const subdomain = window.location.hostname.split(".")[0];
  const canDeleteEnrolments =
    subdomain === "admin" &&
    loginState.group_set.includes("superuser") &&
    !subjectOverviewMode;

  const handleOpenCourseOverview = useCallback(
    (targetCourseId: string) => {
      navigate(getLmsCourseOverviewRoute(mode, targetCourseId));
    },
    [mode, navigate]
  );

  const handleError = useCallback(
    (error: any) => {
      if (onError) {
        onError(error);
        return;
      }
      handleLocalError(error);
    },
    [handleLocalError, onError]
  );

  const isCompletedEnrolment = useCallback(
    (row: any) => row.original?.status === "completed",
    []
  );

  const isStartedEnrolment = useCallback(
    (row: any) => row.original?.status !== "not_started",
    []
  );

  const getStartedEnrolmentDeleteTooltip = useCallback(
    (row: any) =>
      isStartedEnrolment(row)
        ? t("lms_cannot_delete_started_enrolment")
        : undefined,
    [isStartedEnrolment, t]
  );

  const getBulkDeleteStartedEnrolmentTooltip = useCallback(
    (selectedRows: any[]) =>
      selectedRows.some((row) => row.status !== "not_started")
        ? t("lms_bulk_delete_started_enrolment_blocked")
        : undefined,
    [t]
  );

  const enrolmentStatusFilterOptions = useMemo(
    () => [
      { value: "not_started", label: t("not_started") },
      { value: "in_progress", label: t("in_progress") },
      { value: "completed", label: t("completed") },
    ],
    [t]
  );

  const enrolmentTableRowActions = useCallback(
    (row: any) => {
      const targetCourseId = String(
        row.original.course?.id ?? courseId ?? ""
      ).trim();

      return (
        <>
          {targetCourseId && showViewCourseButton && (
            <Tooltip
              arrow
              placement="top"
              enterDelay={1000}
              enterNextDelay={250}
              title={t("view_course")}
            >
              <GenericButton
                variant="icon"
                color="iconBase"
                onClick={() => handleOpenCourseOverview(targetCourseId)}
              >
                <MenuBookIcon />
              </GenericButton>
            </Tooltip>
          )}
        </>
      );
    },
    [courseId, handleOpenCourseOverview, showViewCourseButton, t]
  );

  const baseColumns = useMemo(
    () => [
      {
        header: t("id"),
        accessorKey: "id",
        id: "id",
        visibleInShowHideMenu: mode === "administrator",
      },
      {
        header: t("participant"),
        accessorKey: "participant.name",
        id: "participant",
        customColumnFilterId: "participant_name",
        customSortingKey: "participant__name_order_index",
        accessorFn: (row: any): ReactNode => {
          const subjectId = row.participant?.id;
          const name = row.participant?.name ?? "-";
          if (subjectId) {
            return (
              <NavigateToSubjectOverviewLink
                subjectId={String(subjectId)}
                label={name}
                skipGlobalStateUpdate
              />
            );
          }
          return name;
        },
      },
      ...(mode === "administrator"
        ? [
            {
              header: t("company"),
              accessorKey: "company.name",
              id: "company",
              customColumnFilterId: "company_name",
              customSortingKey: "participant__company__name",
              accessorFn: (row: any): ReactNode => {
                const companySubjectId = row.company?.subject_id;
                const name = row.company?.name ?? "-";
                if (companySubjectId) {
                  return (
                    <NavigateToSubjectOverviewLink
                      subjectId={String(companySubjectId)}
                      label={name}
                      skipGlobalStateUpdate
                    />
                  );
                }
                return name;
              },
            },
          ]
        : []),
      {
        header: t("course_recurrence"),
        accessorKey: "course.has_expirable_certificate",
        id: "course_recurrence",
        customColumnFilterId: "course__has_expirable_certificate",
        filterVariant: "select",
        filterSelectOptions: [
          { value: "false", label: t("one_time") },
          { value: "true", label: t("recurring") },
        ],
        setNoFilterParameter: true,
        enableSorting: false,
        accessorFn: (row: any) =>
          row.course?.has_expirable_certificate
            ? t("recurring")
            : t("one_time"),
      },
      {
        header: t("previously_completed"),
        accessorKey: "previously_completed",
        id: "previously_completed",
        setNoFilterParameter: true,
        muiFilterTextFieldProps: {
          type: "number",
          inputProps: { min: 1 },
        },
      },
      {
        header: t("status"),
        accessorKey: "status",
        id: "status",
        filterVariant: "select",
        filterSelectOptions: enrolmentStatusFilterOptions,
        setNoFilterParameter: true,
        accessorFn: (row: any) => (row.status ? t(row.status) : "-"),
      },
      {
        header: t("progress"),
        accessorKey: "progress_percentage",
        id: "progress_percentage",
        setNoFilterParameter: true,
        muiFilterTextFieldProps: {
          type: "number",
          inputProps: { min: 0, max: 100 },
        },
        accessorFn: (row: any): string =>
          row.progress_percentage != null && row.progress_percentage !== ""
            ? `${row.progress_percentage}%`
            : "-",
      },
      {
        header: t("completion_interval_in_days"),
        accessorKey: "completion_interval_in_days",
        id: "completion_interval_in_days",
        setNoFilterParameter: true,
        muiFilterTextFieldProps: {
          type: "number",
          inputProps: { min: 0 },
        },
        accessorFn: (row: any): ReactNode => (
          <LmsIntervalValue
            value={row.completion_interval_in_days}
            inheritedFrom={getEnrolmentTableIntervalInheritedFrom(
              row.inherited_from?.completion_interval_in_days
            )}
          />
        ),
      },
      {
        header: t("validity_interval_in_days"),
        accessorKey: "validity_interval_in_days",
        id: "validity_interval_in_days",
        setNoFilterParameter: true,
        muiFilterTextFieldProps: {
          type: "number",
          inputProps: { min: 0 },
        },
        accessorFn: (row: any): ReactNode => (
          <LmsIntervalValue
            value={row.validity_interval_in_days}
            inheritedFrom={getEnrolmentTableIntervalInheritedFrom(
              row.inherited_from?.validity_interval_in_days
            )}
          />
        ),
      },
      {
        header: t("assigned_on"),
        accessorKey: "created_on",
        id: "created_on",
        filterVariant: "date",
        accessorFn: (row: any) => formatDateValue(row.created_on, true),
      },
      {
        header: t("assigned_by"),
        accessorKey: "created_by.name",
        id: "created_by",
        customColumnFilterId: "created_by_name",
        accessorFn: (row: any): ReactNode => row.created_by?.name ?? "-",
      },
      {
        header: t("started_on"),
        accessorKey: "started_on",
        id: "started_on",
        filterVariant: "date",
        sortingFn: "datetime",
        accessorFn: (row: any) => formatDateValue(row.started_on, true),
      },
      {
        header: t("completed_on"),
        accessorKey: "completed_on",
        id: "completed_on",
        filterVariant: "date",
        sortingFn: "datetime",
        accessorFn: (row: any) => formatDateValue(row.completed_on, true),
      },
      {
        header: t("last_downloaded_on"),
        accessorKey: "certificate.last_downloaded_on",
        id: "certificate__last_downloaded_on",
        filterVariant: "date",
        sortingFn: "datetime",
        accessorFn: (row: any) =>
          formatDateValue(row.certificate?.last_downloaded_on, true),
      },
      {
        header: t("modified_on"),
        accessorKey: "modified_on",
        id: "modified_on",
        filterVariant: "date",
        sortingFn: "datetime",
        accessorFn: (row: any): ReactNode =>
          formatDateValue(row.modified_on, true),
      },
      {
        header: t("modified_by"),
        accessorKey: "modified_by.name",
        id: "modified_by",
        customColumnFilterId: "modified_by_name",
        enableSorting: false,
        accessorFn: (row: any): ReactNode => row.modified_by?.name ?? "-",
      },
    ],
    [enrolmentStatusFilterOptions, mode, t]
  );

  const columns = useMemo(() => {
    const visibleBaseColumns = subjectOverviewMode
      ? baseColumns.filter((column) => column.id !== "participant")
      : baseColumns;

    if (!includeCourseColumn) {
      return visibleBaseColumns;
    }

    const courseColumn = {
      header: t("course_title"),
      accessorKey: "course.title",
      id: "course",
      customColumnFilterId: "course__title",
      accessorFn: (row: any) =>
        getLmsDisplayText(row.course?.title ?? row.course?.name) || "-",
    };

    return [
      visibleBaseColumns[0],
      courseColumn,
      ...visibleBaseColumns.slice(1),
    ];
  }, [baseColumns, subjectOverviewMode, includeCourseColumn, t]);

  const overviewScope = useMemo(
    () =>
      resolveOverviewMetricsScope({
        subdomain,
        mode,
        subjectSearch,
        selectedChildCompanySubjectId: selectedChildCompany.subjectId,
      }),
    [mode, selectedChildCompany.subjectId, subdomain, subjectSearch]
  );

  const needsCompanySelection = requiresLmsCompanySelection({
    subdomain,
    mode,
    subjectOverviewMode,
    courseId,
    participantId,
  });
  const selectedCompanySubjectIdForFiltering =
    needsCompanySelection && overviewScope.companySubjectId
      ? overviewScope.companySubjectId
      : undefined;
  const showTableContent =
    !needsCompanySelection ||
    !selectedCompanySubjectIdForFiltering ||
    companySelectionValidationState === "valid";
  const shouldShowCompanySearch = needsCompanySelection;

  const additionalUrlParameter = useMemo(
    () => ({
      ...(courseId ? { course_id: courseId } : {}),
      ...(participantId ? { participant_id: participantId } : {}),
      ...getLmsManagerRequestParameters(mode, selectedChildCompany.subjectId),
      ...(mode === "manager" || mode === "administrator"
        ? { prefer_translation: "true" }
        : {}),
      ...(selectedCompanySubjectIdForFiltering
        ? { company_subject: selectedCompanySubjectIdForFiltering }
        : {}),
    }),
    [
      courseId,
      mode,
      participantId,
      selectedCompanySubjectIdForFiltering,
      selectedChildCompany.subjectId,
    ]
  );

  const enrolmentExportParameters = useMemo(
    () => ({
      ...(courseId ? { course_id: courseId } : {}),
      ...(participantId ? { participant_id: participantId } : {}),
      ...getLmsManagerRequestParameters(mode, selectedChildCompany.subjectId),
      ...(selectedCompanySubjectIdForFiltering && !exportAllCompanies
        ? { company_subject: selectedCompanySubjectIdForFiltering }
        : {}),
    }),
    [
      courseId,
      exportAllCompanies,
      mode,
      participantId,
      selectedCompanySubjectIdForFiltering,
      selectedChildCompany.subjectId,
    ]
  );

  const topToolbarAction = useCallback(() => {
    const shouldShowExport = !subjectOverviewMode && mode !== "learner";
    const isEnrolmentCreationDisabled =
      !subjectOverviewMode && Boolean(onCreate) && !canCreateEnrolments;

    return (
      <>
        {isEnrolmentCreationDisabled && (
          <Tooltip
            arrow
            placement="bottom"
            title={createEnrolmentsDisabledMessage ?? ""}
          >
            <span>
              <GenericButton disabled startIcon={<AddIcon />}>
                {t("add")}
              </GenericButton>
            </span>
          </Tooltip>
        )}
        {shouldShowExport && (
          <ExportMenu
            entity="enrolment"
            exportParameter="export"
            parametersToRender={enrolmentExportParameters}
            handleError={handleError}
            shouldNotImpersonate
            additionalCheckboxOption={
              selectedCompanySubjectIdForFiltering
                ? {
                    label: "export_all_companies",
                    checked: exportAllCompanies,
                    onChange: setExportAllCompanies,
                  }
                : undefined
            }
            getConfirmationMessage={
              selectedCompanySubjectIdForFiltering
                ? (format) =>
                    t(
                      exportAllCompanies
                        ? "confirm_export_enrolments_all_companies"
                        : "confirm_export_enrolments_selected_company",
                      {
                        company: subjectSearch?.label,
                        fileformat: format,
                      }
                    )
                : undefined
            }
            menuItems={[
              { label: "export_as", format: "csv", icon: "text" },
              { label: "export_as", format: "xlsx", icon: "chart" },
            ]}
          />
        )}
        {customTopToolBarAction?.()}
      </>
    );
  }, [
    customTopToolBarAction,
    canCreateEnrolments,
    createEnrolmentsDisabledMessage,
    enrolmentExportParameters,
    exportAllCompanies,
    handleError,
    mode,
    selectedCompanySubjectIdForFiltering,
    subjectSearch?.label,
    subjectOverviewMode,
    t,
  ]);

  return (
    <>
      {shouldShowCompanySearch && (
        <div style={{ width: "50%", paddingBottom: "1rem" }}>
          <FormFieldLabel
            label={t("company")}
            htmlFor="lms-enrolments-company-filter"
          />
          <DropDownSubjectSearch
            name="lms-enrolments-company-filter"
            subdomain={subdomain}
            options={[]}
            onChange={() => setExportAllCompanies(false)}
            useGlobalState={true}
            isClearable={true}
            queryParameter="companies"
            selectionEnrichment={LMS_START_VERTRAG_SELECTION_ENRICHMENT}
            getSelectionBlockedMessage={(selection) =>
              getLmsSelectionBlockedMessage(selection, t)
            }
            onSelectionValidationChange={setCompanySelectionValidationState}
          />
        </div>
      )}
      {showTableContent && (
        <GenericCrudTable
          key={`enrolments-table-${courseId ?? "all"}-${
            participantId ?? "all-participants"
          }-${refreshKey}-${
            selectedCompanySubjectIdForFiltering ?? "all-companies"
          }-${includeCourseColumn ? "with-course" : "no-course"}-${
            subjectOverviewMode ? "no-participant" : "with-participant"
          }`}
          dataTestId={
            mode === "manager"
              ? "lms-manager-enrolments-table"
              : "lms-admin-enrolments-table"
          }
          entity="enrolment"
          columns={columns}
          additionalUrlParameter={additionalUrlParameter}
          customCreateHandler={subjectOverviewMode ? undefined : onCreate}
          customEditHandler={(row: any) => {
            const enrolment = row?.original ?? row;
            if (enrolment.status !== "completed") {
              setSelectedEnrolmentForEdit(enrolment);
            }
          }}
          customTopToolBarAction={topToolbarAction}
          disableAddItem={
            subjectOverviewMode ? true : !onCreate || !canCreateEnrolments
          }
          hideEditButtonForRows={mode === "learner" || subjectOverviewMode}
          disableEditButtonForRow={isCompletedEnrolment}
          disallowDelete={!canDeleteEnrolments}
          hideDeleteButtonForRows={!canDeleteEnrolments}
          disableDeleteButtonForRow={isStartedEnrolment}
          getDeleteButtonDisabledTooltip={getStartedEnrolmentDeleteTooltip}
          getBulkDeleteDisabledTooltip={getBulkDeleteStartedEnrolmentTooltip}
          customRowActions={enrolmentTableRowActions}
          refreshTableAfterAction={onRefreshed}
          enableRowSelection={!subjectOverviewMode}
          enableBulkDelete={canDeleteEnrolments}
          allowColumnFiltering
          showColumnFilterbyDefault
          columnMinSize={40}
          disableGlobalFilter
          rightAlignTopToolbarActions={subjectOverviewMode}
          tableCollapseWidth={subjectOverviewMode ? "90%" : undefined}
          customDensity={subjectOverviewMode ? "compact" : undefined}
          customPageSize={subjectOverviewMode ? 5 : undefined}
          customColumnVisibility={{
            created_on: true,
            created_by: true,
          }}
        />
      )}

      <LmsEnrolmentIntervalEditModal
        open={Boolean(selectedEnrolmentForEdit)}
        enrolment={selectedEnrolmentForEdit}
        mode={mode}
        selectedCompanySubjectId={selectedChildCompany.subjectId}
        onClose={() => setSelectedEnrolmentForEdit(null)}
        onCompleted={() => {
          setSelectedEnrolmentForEdit(null);
          onRefreshed?.();
        }}
      />

      {errorModal}
    </>
  );
}
